/**
 * Levels 1–6 (Grades 1–3): gentle, welcoming outdoor places. Lots of sky,
 * soft greens and sunny yellows, one clear landmark per scene.
 */
import type { ThemePack } from '../types.ts';
import { CLOUD_WHITE, PARROTS, TRUNK } from './shared.ts';

const sunnyMeadow: ThemePack = {
  id: 'sunny-meadow',
  level: 1,
  name: { en: 'Sunny Meadow', af: 'Sonnige Weiveld' },
  tagline: { en: 'Where every sum starts with sunshine.', af: 'Waar elke som met sonskyn begin.' },
  backgroundConcept:
    'Rolling green hills under a big friendly sun. Rows of round fruit trees, a wildflower meadow in front and fat cotton-wool clouds drifting past. The most welcoming scene of all: lots of open sky, soft greens and buttery yellows.',
  uiNotes: 'Crisp white panels with navy text, a sunflower-yellow block border and grass-green buttons. Warm fieldstone bricks with cream mortar.',
  animationIdeas: [
    'Clouds drift slowly across the sky',
    'Sun rays turn very slowly',
    'Butterflies flutter over the meadow',
    'Hills shift a few pixels with the parallax drift',
  ],
  soundNotes: 'C major pentatonic marimba at an easy 84 bpm over a warm pad, with soft birdsong. The "home" sound of the game.',
  palette: {
    skyTop: '#5cc8ff',
    skyBottom: '#c9f0ff',
    wellBg: '#ffffff59',
    wellBorder: '#ffffffcc',
    blockBg: '#fffdf3',
    blockBorder: '#ffb703',
    blockText: '#1d2a44',
    brickA: '#e0b48a',
    brickB: '#cf9d72',
    brickMortar: '#fff3dd',
    uiPanel: '#ffffffe6',
    uiText: '#1d2a44',
    uiMuted: '#5b6780',
    accent: '#ff8a3d',
    buttonBg: '#1b8046',
    buttonText: '#ffffff',
    good: '#2fb35a',
    bad: '#ef6f6c',
  },
  brickStyle: 'stone',
  particles: { kind: 'butterflies', density: 0.45, color: '#ffa8d6' },
  scene: [
    { kind: 'sky', mid: '#a8e4ff', midAt: 0.5 },
    { kind: 'sun', x: 0.82, y: 0.13, radius: 0.075, color: '#ffe45c', glow: '#fff5b8', rays: 12, rayColor: '#fff8d1' },
    { kind: 'clouds', count: 5, yMin: 0.06, yMax: 0.34, size: 0.24, color: CLOUD_WHITE, shade: '#d3ecff', speed: 0.8, depth: 0.05 },
    { kind: 'ridges', shape: 'rolling', baseline: 0.64, height: 0.12, color: '#a6e08f', roughness: 0.3, mist: 0.3, depth: 0.1 },
    { kind: 'trees', species: 'round', count: 7, baseline: 0.655, size: 0.07, color: '#7fcd70', trunk: '#9a6b47', depth: 0.1 },
    { kind: 'ridges', shape: 'rolling', baseline: 0.75, height: 0.12, color: '#82d26b', roughness: 0.4, depth: 0.4 },
    { kind: 'trees', species: 'round', count: 4, baseline: 0.775, size: 0.15, spread: [0, 1], color: '#5dbb5a', trunk: TRUNK, accent: '#ff6b6b', depth: 0.4 },
    {
      kind: 'ground',
      top: 0.86,
      color: '#6fcb5b',
      texture: 'meadow',
      accent: '#4ea846',
      flowers: ['#ff8fab', '#ffd43b', '#ffffff', '#b197fc'],
      depth: 0.75,
    },
  ],
  audio: { key: 'C', scale: 'majorPentatonic', bpm: 84, lead: 'marimba', pad: 'warm', ambience: 'birds' },
};

const jungleCanopy: ThemePack = {
  id: 'jungle-canopy',
  level: 2,
  name: { en: 'Jungle Canopy', af: 'Oerwoud-blaredak' },
  tagline: { en: 'Swing through the leafy treetops!', af: 'Swaai deur die blaarryke boomtoppe!' },
  backgroundConcept:
    'Looking up into a sunlit rainforest: misty green hills behind, tall jungle trees, sunbeams slanting through the leaves, flowering vines dangling from a leafy fringe at the top and big ferns swaying in front.',
  uiNotes: 'Pale leaf-green panels, deep forest-green text and buttons, amber accent like sunlight on leaves. Mossy stone bricks.',
  animationIdeas: ['Vines sway on a slow breeze', 'Sunbeams shimmer in and out', 'Ferns rock gently', 'Leaves tumble down from the canopy'],
  soundNotes: 'D mixolydian kalimba at 88 bpm, warm pad, soft insect chirps and the odd far-off bird call.',
  palette: {
    skyTop: '#4fc3e8',
    skyBottom: '#d4f7d9',
    wellBg: '#ffffff5c',
    wellBorder: '#f3ffe8cc',
    blockBg: '#fbfff4',
    blockBorder: '#7ed957',
    blockText: '#15311f',
    brickA: '#8fae6b',
    brickB: '#7a9a5a',
    brickMortar: '#e3f0c8',
    uiPanel: '#f7fff2eb',
    uiText: '#15311f',
    uiMuted: '#4f6b58',
    accent: '#ffb400',
    buttonBg: '#1b7a43',
    buttonText: '#ffffff',
    good: '#27a55a',
    bad: '#e8665f',
  },
  brickStyle: 'mossy',
  particles: { kind: 'leaves', density: 0.5, color: '#7fd36b' },
  scene: [
    { kind: 'sky', mid: '#b7f0e0', midAt: 0.6 },
    { kind: 'lightRays', x: 0.28, count: 6, spread: 0.9, color: '#fff6c4', opacity: 0.35 },
    { kind: 'clouds', count: 3, yMin: 0.1, yMax: 0.3, size: 0.2, color: CLOUD_WHITE, shade: '#d5f3e6', speed: 0.5, depth: 0.05 },
    { kind: 'ridges', shape: 'rolling', baseline: 0.6, height: 0.16, color: '#8fd6a0', roughness: 0.6, mist: 0.45, depth: 0.1 },
    { kind: 'trees', species: 'jungle', count: 6, baseline: 0.64, size: 0.22, color: '#6cc27c', trunk: '#8a6a4a', depth: 0.1 },
    { kind: 'trees', species: 'jungle', count: 4, baseline: 0.82, size: 0.36, color: '#43a85a', trunk: '#7a5236', accent: '#ff6fa0', depth: 0.45 },
    { kind: 'ground', top: 0.9, color: '#4fb04a', texture: 'moss', accent: '#3d9440', depth: 0.8 },
    { kind: 'trees', species: 'fern', count: 7, baseline: 0.93, size: 0.14, color: '#57bb4f', trunk: '#3f8a3a', sway: true, depth: 0.8 },
    { kind: 'vines', count: 9, length: 0.38, color: '#3f8f3a', leaf: '#62c054', flower: '#ff7eb6', canopy: '#3fa04a', depth: 0.9 },
  ],
  audio: { key: 'D', scale: 'mixolydian', bpm: 88, lead: 'kalimba', pad: 'warm', ambience: 'insects' },
};

const parrotCove: ThemePack = {
  id: 'parrot-cove',
  level: 3,
  name: { en: 'Parrot Cove', af: 'Papegaaibaai' },
  tagline: { en: 'Sandy toes and chatty parrots.', af: 'Sand tussen die tone en babbelende papegaaie.' },
  backgroundConcept:
    'A turquoise cove framed by a honey-coloured sea cliff, a green headland on the horizon and a white-sand beach with leaning palms. Scarlet, yellow and blue parrots flap across the sky.',
  uiNotes: 'Sea-blue buttons, coral accent and border, white panels with deep-sea text. Shell-pink bricks.',
  animationIdeas: ['Waves roll and sparkle', 'Palms sway in the sea breeze', 'Parrots flap across the sky', 'Bright feathers float down'],
  soundNotes: 'F major pentatonic steel pan at a bouncy 96 bpm, airy pad, gentle lapping waves.',
  palette: {
    skyTop: '#3fbff5',
    skyBottom: '#bff4ff',
    wellBg: '#ffffff59',
    wellBorder: '#ffffffcc',
    blockBg: '#fffcf0',
    blockBorder: '#ff6f59',
    blockText: '#12304a',
    brickA: '#ffd6b8',
    brickB: '#ffc39a',
    brickMortar: '#fff7ee',
    uiPanel: '#ffffffe6',
    uiText: '#12304a',
    uiMuted: '#56708a',
    accent: '#ff6f59',
    buttonBg: '#0b7fa6',
    buttonText: '#ffffff',
    good: '#22a86b',
    bad: '#f0645c',
  },
  brickStyle: 'shell',
  particles: { kind: 'feathers', density: 0.3, color: '#ff7a59' },
  scene: [
    { kind: 'sky', mid: '#8fe1ff', midAt: 0.55 },
    { kind: 'sun', x: 0.18, y: 0.12, radius: 0.07, color: '#ffe45c', glow: '#fff3b0', rays: 10, rayColor: '#fff7cf' },
    { kind: 'clouds', count: 4, yMin: 0.05, yMax: 0.3, size: 0.22, color: CLOUD_WHITE, shade: '#cdeeff', speed: 0.9, depth: 0.05 },
    { kind: 'ridges', shape: 'rolling', baseline: 0.58, height: 0.1, color: '#94dca0', roughness: 0.5, mist: 0.3, depth: 0.1 },
    { kind: 'water', top: 0.58, color: '#34c6d9', deep: '#1aa1c9', foam: '#ffffff', waves: 0.6, sparkle: true, depth: 0.2 },
    { kind: 'cliffs', side: 'right', width: 0.22, top: 0.3, color: '#e0b890', stripes: '#c99a70', moss: '#6fcf6a', depth: 0.45 },
    { kind: 'ground', top: 0.8, color: '#ffe0a3', texture: 'sand', accent: '#f2c77e', depth: 0.6 },
    {
      kind: 'trees',
      species: 'palm',
      count: 3,
      baseline: 0.84,
      size: 0.34,
      spread: [-0.05, 0.4],
      color: '#55c25a',
      trunk: '#b07a4a',
      accent: '#8a5a2b',
      sway: true,
      depth: 0.6,
    },
    { kind: 'birds', style: 'parrot', count: 4, yMin: 0.15, yMax: 0.45, size: 0.06, colors: PARROTS, speed: 5, depth: 0.5 },
  ],
  audio: { key: 'F', scale: 'majorPentatonic', bpm: 96, lead: 'steelpan', pad: 'airy', ambience: 'waves' },
};

const waterfallGorge: ThemePack = {
  id: 'waterfall-gorge',
  level: 4,
  name: { en: 'Waterfall Gorge', af: 'Watervalkloof' },
  tagline: { en: 'Follow the roar to the rainbow in the spray.', af: 'Volg die gedruis na die reënboog in die sproei.' },
  backgroundConcept:
    'A deep, sunny gorge: warm grey cliffs with mossy ledges frame a tall waterfall pouring off a flat-topped rock wall into a clear pool. A rainbow hangs in the spray and snowy peaks peek over the rim.',
  uiNotes: 'Cool white panels, deep-blue buttons, teal accent. Blue-grey river-stone bricks.',
  animationIdeas: ['Water streams down the fall', 'Foam puffs at the base', 'Fine spray drifts upward', 'Ferns nod on the ledge'],
  soundNotes: 'G major plucked strings at a relaxed 80 bpm, airy pad, a soft running-stream bed.',
  palette: {
    skyTop: '#56c2f0',
    skyBottom: '#d6f5ff',
    wellBg: '#ffffff5c',
    wellBorder: '#ffffffcc',
    blockBg: '#f8fdff',
    blockBorder: '#38b6ff',
    blockText: '#14304a',
    brickA: '#a9b4c2',
    brickB: '#94a1b2',
    brickMortar: '#eef3f8',
    uiPanel: '#ffffffe6',
    uiText: '#14304a',
    uiMuted: '#566b82',
    accent: '#2bb3a0',
    buttonBg: '#1769aa',
    buttonText: '#ffffff',
    good: '#23a55a',
    bad: '#ea6a62',
  },
  brickStyle: 'stone',
  particles: { kind: 'spray', density: 0.5, color: '#ffffff' },
  scene: [
    { kind: 'sky', mid: '#aee6ff', midAt: 0.5 },
    { kind: 'sun', x: 0.5, y: 0.1, radius: 0.06, color: '#ffe66d', glow: '#fff6c2' },
    { kind: 'clouds', count: 4, yMin: 0.05, yMax: 0.24, size: 0.2, color: CLOUD_WHITE, shade: '#d0ecff', speed: 0.6, depth: 0.05 },
    { kind: 'ridges', shape: 'peaks', baseline: 0.42, height: 0.22, color: '#a9cfe0', snow: '#ffffff', snowLine: 0.7, mist: 0.5, depth: 0.05 },
    { kind: 'ridges', shape: 'table', baseline: 0.62, height: 0.32, color: '#b9aa9c', stripes: '#a89886', roughness: 0.3, depth: 0.1 },
    { kind: 'waterfall', x: 0.5, top: 0.3, bottom: 0.8, width: 0.14, color: '#7fd8ff', foam: '#ffffff', rock: '#a8988a', depth: 0.1 },
    { kind: 'rainbow', x: 0.5, y: 0.82, radius: 0.26, width: 0.06, opacity: 0.45, depth: 0.1 },
    { kind: 'cliffs', side: 'left', width: 0.3, top: 0.18, color: '#b7a79a', stripes: '#a0907f', moss: '#6cc36a', depth: 0.45 },
    { kind: 'cliffs', side: 'right', width: 0.3, top: 0.22, color: '#b7a79a', stripes: '#a0907f', moss: '#6cc36a', depth: 0.45 },
    { kind: 'water', top: 0.8, color: '#56c9e8', deep: '#2fa7d0', foam: '#ffffff', waves: 0.4, sparkle: true, depth: 0.45 },
    { kind: 'ground', top: 0.9, color: '#a99b8c', texture: 'rock', accent: '#8f8173', depth: 0.8 },
    { kind: 'trees', species: 'fern', count: 6, baseline: 0.93, size: 0.12, color: '#5cc05a', trunk: '#3e8c3a', sway: true, depth: 0.8 },
  ],
  audio: { key: 'G', scale: 'major', bpm: 80, lead: 'pluck', pad: 'airy', ambience: 'stream' },
};

const mushroomGlade: ThemePack = {
  id: 'mushroom-glade',
  level: 5,
  name: { en: 'Mushroom Glade', af: 'Paddastoel-oopte' },
  tagline: { en: 'Tiptoe past the giant toadstools.', af: 'Sluip op jou tone verby die reuse-paddastoele.' },
  backgroundConcept:
    'A storybook woodland clearing: giant red-and-white and orange toadstools, round trees on pastel hills and a carpet of tiny flowers. The sky blushes from blue to candy pink near the horizon.',
  uiNotes: 'Soft pink-white panels, plum text, purple buttons and a bubblegum accent. Wooden log bricks.',
  animationIdeas: ['Glowing pollen motes float upward', 'Clouds drift past', 'Sun rays turn slowly'],
  soundNotes: 'A lydian bells at 82 bpm with a glassy pad for a hint of magic, over very soft crickets.',
  palette: {
    skyTop: '#7cc9ff',
    skyBottom: '#ffe3f1',
    wellBg: '#ffffff5c',
    wellBorder: '#fff4fbcc',
    blockBg: '#fffaf6',
    blockBorder: '#ff7eb6',
    blockText: '#2a1f3d',
    brickA: '#c98b5a',
    brickB: '#b57649',
    brickMortar: '#fbe8d3',
    uiPanel: '#fffaffe8',
    uiText: '#2a1f3d',
    uiMuted: '#6a5b7d',
    accent: '#ff5c8a',
    buttonBg: '#8a3fb8',
    buttonText: '#ffffff',
    good: '#2fa65d',
    bad: '#e6605d',
  },
  brickStyle: 'wood',
  particles: { kind: 'pollen', density: 0.55, color: '#fff3a3' },
  scene: [
    { kind: 'sky', mid: '#dff1ff', midAt: 0.5 },
    { kind: 'sun', x: 0.2, y: 0.14, radius: 0.06, color: '#fff08a', glow: '#fff7cc', rays: 8, rayColor: '#fffbe0' },
    { kind: 'clouds', count: 4, yMin: 0.06, yMax: 0.3, size: 0.2, color: CLOUD_WHITE, shade: '#f3dcf0', speed: 0.6, depth: 0.05 },
    { kind: 'ridges', shape: 'rolling', baseline: 0.62, height: 0.12, color: '#b5e6a8', roughness: 0.4, mist: 0.4, depth: 0.1 },
    { kind: 'trees', species: 'round', count: 6, baseline: 0.64, size: 0.08, color: '#9bd99a', trunk: '#a57b5a', depth: 0.1 },
    { kind: 'ridges', shape: 'rolling', baseline: 0.76, height: 0.1, color: '#8fd98a', roughness: 0.3, depth: 0.4 },
    { kind: 'trees', species: 'mushroom', count: 5, baseline: 0.8, size: 0.17, color: '#ff5d73', trunk: '#fff1dc', accent: '#ffffff', depth: 0.4 },
    {
      kind: 'ground',
      top: 0.87,
      color: '#7fd17a',
      texture: 'meadow',
      accent: '#5fb35a',
      flowers: ['#ffffff', '#ffd6e8', '#fff3a3', '#c5b3ff'],
      depth: 0.75,
    },
    { kind: 'trees', species: 'mushroom', count: 4, baseline: 0.95, size: 0.1, color: '#ffa94d', trunk: '#fff4e0', accent: '#ffffff', depth: 0.75 },
  ],
  audio: { key: 'A', scale: 'lydian', bpm: 82, lead: 'bell', pad: 'glass', ambience: 'insects' },
};

const savannaSunrise: ThemePack = {
  id: 'savanna-sunrise',
  level: 6,
  name: { en: 'Savanna Sunrise', af: 'Savanne-sonop' },
  tagline: { en: 'Golden grass, baobabs and a brand-new day.', af: "Goue gras, kremetartbome en 'n splinternuwe dag." },
  backgroundConcept:
    'South African bushveld at dawn: a huge golden sun rising behind flat-topped koppies, umbrella acacias and a big baobab (kremetartboom) on golden grass, and a cluster of thatched rondavels. Birds glide across a peach-and-blue sky.',
  uiNotes: 'Warm cream panels, dark cocoa text, marigold border and burnt-orange accent. Terracotta clay bricks.',
  animationIdeas: ['The rising sun glows and its rays turn slowly', 'Birds glide across the dawn sky', 'Golden pollen drifts in the light', 'Thin clouds stretch along the horizon'],
  soundNotes: 'E major pentatonic marimba at 92 bpm, warm pad, dawn birdsong.',
  palette: {
    skyTop: '#7ccfff',
    skyBottom: '#ffd29a',
    wellBg: '#fffaf05c',
    wellBorder: '#fff4e0cc',
    blockBg: '#fffaf0',
    blockBorder: '#f59f00',
    blockText: '#2e1d0e',
    brickA: '#d9895b',
    brickB: '#c4744a',
    brickMortar: '#fde9d0',
    uiPanel: '#fffaf2e8',
    uiText: '#2e1d0e',
    uiMuted: '#6e5a48',
    accent: '#f76707',
    buttonBg: '#b8480f',
    buttonText: '#ffffff',
    good: '#2b9a52',
    bad: '#e05d52',
  },
  brickStyle: 'clay',
  particles: { kind: 'pollen', density: 0.35, color: '#ffe8a3' },
  scene: [
    { kind: 'sky', mid: '#ffe7c2', midAt: 0.55, glow: { x: 0.72, y: 0.5, radius: 0.6, color: '#fff0c2' } },
    { kind: 'sun', x: 0.72, y: 0.47, radius: 0.12, color: '#ffd166', glow: '#ffe8a8', rays: 16, rayColor: '#fff1c7' },
    { kind: 'clouds', count: 3, yMin: 0.08, yMax: 0.3, size: 0.28, color: '#fff8ef', shade: '#ffd8c2', speed: 0.5, style: 'long', depth: 0.05 },
    { kind: 'birds', style: 'gull', count: 5, yMin: 0.18, yMax: 0.34, size: 0.04, colors: ['#7a4e3a'], speed: 3, depth: 0.1 },
    { kind: 'ridges', shape: 'mesa', baseline: 0.6, height: 0.08, color: '#d9b27d', stripes: '#caa06c', roughness: 0.3, mist: 0.4, depth: 0.1 },
    { kind: 'ridges', shape: 'rolling', baseline: 0.7, height: 0.06, color: '#f0c774', roughness: 0.3, depth: 0.35 },
    { kind: 'trees', species: 'acacia', count: 3, baseline: 0.715, size: 0.16, spread: [0.3, 1.05], color: '#8fbf4a', trunk: '#6e4b33', depth: 0.35 },
    { kind: 'trees', species: 'baobab', count: 1, baseline: 0.745, size: 0.3, spread: [0.1, 0.22], color: '#8cbf57', trunk: '#b08968', depth: 0.35 },
    {
      kind: 'structure',
      type: 'huts',
      x: 0.84,
      baseline: 0.75,
      size: 0.11,
      color: '#e8b27c',
      accent: '#d9a441',
      trim: '#b5523b',
      mound: '#e7bd66',
      depth: 0.35,
    },
    { kind: 'ground', top: 0.85, color: '#e8b85c', texture: 'grass', accent: '#c99a3e', depth: 0.75 },
  ],
  audio: { key: 'E', scale: 'majorPentatonic', bpm: 92, lead: 'marimba', pad: 'warm', ambience: 'birds' },
};

export const LEVELS_01_06: ThemePack[] = [sunnyMeadow, jungleCanopy, parrotCove, waterfallGorge, mushroomGlade, savannaSunrise];
